import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Autocomplete,
  createFilterOptions,
} from "@mui/material";
import './DynamicFormModal.scss'


import ReactQuill from "react-quill";
import "./DynamicFormModal.scss";
import { getBootCampsList } from "../../services/dynamicForms";
import { useSelector } from "react-redux";
import Loader from "../Loader/Loader";
import SimpleAutoComplete from "../SimpleAutoComplete/SimpleAutoComplete";
import dayjs from "dayjs";


const filter = createFilterOptions();


const FormInputModal = ({
  open, handleClose, initialData, onSubmit, isClone
}) => {
  const [formData, setFormData] = useState({
    name: "",
    type: null,
    description: "",
    associatedBootcamps: [],
    published: false,
  });
  const [errors, setErrors] = useState({});
  const [bootcampOptions, setBootcampOptions] = useState([]);
  const submitLoader = useSelector((state) => state?.forms.loading);

  const typeOptions = useSelector(
    (state) => state?.utility?.constants?.formType
  );

  const selectedType =
    typeOptions?.find(
      (opt) =>
        opt.key === formData.type || opt.value === formData.type
    ) || null;

  useEffect(() => {
    if (initialData) {
      const bootcamps = isClone ? [] : initialData.associatedBootcamps;
      const formName = isClone ? `copy of ${initialData.name}` : initialData.name;
      setFormData({
        formId: initialData.id,
        name: formName || "",
        type: initialData.type || null,
        description: initialData.description || "",
        published: initialData.published,
        associatedBootcamps: bootcamps || [],
      });
    } else {
      setFormData({
        name: "",
        type: null,
        description: "",
        associatedBootcamps: [],
        published: false,
      });
    }
  }, [initialData]);

  useEffect(() => {
    async function fetchBootcamps() {
      try {
        let response;
        if (isClone) {
          response = await getBootCampsList(initialData.id);
        } else {
          response = await getBootCampsList();
        }
        setBootcampOptions(response?.data?.results || []);
      } catch (err) {
        console.error("Failed to fetch bootcamps:", err);
      }
    }
    fetchBootcamps();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBootcampChipDelete = (bootcamp) => {
    const newList = formData.associatedBootcamps.filter(
      (b) => b.id !== bootcamp.id
    );
    handleChange("associatedBootcamps", newList);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.type) newErrors.type = "Type is required";
    const rawDescription = formData.description.trim();
    const plainText = rawDescription.replace(/<[^>]*>/g, "");
    if (!rawDescription || rawDescription === "<p><br></p>") {
      newErrors.description = "Description is required";
    } else if (plainText.length > 500) {
      newErrors.description = `Description cannot exceed 500 characters (Current: ${plainText.length})`;
    }
    if (!isClone && formData.associatedBootcamps.length === 0)
      newErrors.associatedBootcamps = "Select at least one Bootcamp";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const sanitizedDescription = DOMPurify.sanitize(formData.description, {
      USE_PROFILES: { html: true },
    });

    const formattedData = {
      ...formData,
      description: sanitizedDescription,
    };
    onSubmit(formattedData);
  };

  const handleBootcampChange = (event, newValue) => {
    if (newValue.find((v) => v.all)) {
      const isAllSelected =
        formData.associatedBootcamps.length === bootcampOptions.length;
      handleChange("associatedBootcamps", isAllSelected ? [] : bootcampOptions);
    } else {
      const uniqueValues = newValue.filter(
        (item, index, self) => index === self.findIndex((t) => t.id === item.id)
      );
      handleChange("associatedBootcamps", uniqueValues);
    }
  };
  const getDialogTitle = () => {
    if (isClone) return "Clone Form";
    return initialData ? "Edit Form" : "Create Form";
  };
  return (
    <>
      {submitLoader && <Loader />}
      <Dialog
        open={open}
        onClose={handleClose}
        className="modal-drawer-container"
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: "1rem",
            boxShadow: "0rem 0.6rem 3rem 0rem #0D0D0D",
            zIndex: 1300,
          },
        }}
      >
        <DialogTitle className="dialog-title">
          {getDialogTitle()}
        </DialogTitle>

        <DialogContent className="dialog-content">
          <Box className="width-100">
            <TextField
              className="text-input"
              label="Form Name "
              fullWidth
              size="small"
              color="secondary"
              required
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              margin="dense"
            />

            <SimpleAutoComplete
              label="Form Type"
              placeholder="Select Form Type"
              options={typeOptions || []}
              value={selectedType ?? null}
              disabled={Boolean(initialData?.id)}
              onChange={(event, newValue) => {
                handleChange("type", newValue ? newValue.key : "");
              }}
              getOptionLabel={(option) => option?.value ?? ""}
              required
              error={!!errors?.type}
              helperText={errors?.type}
              renderOption={(props, option) => {
                const isSelected = selectedType?.key === option.key;
                return (
                  <li
                    {...props}
                    key={option.key}
                    className={isSelected ? "multi-tag-edit-list-selected" : "multi-tag-style"}
                  >
                    <Box lineHeight={1.1}>
                      <Box>{option.value}</Box>
                    </Box>
                  </li>
                );
              }}
            />

            <Autocomplete
              multiple
              disableCloseOnSelect
              sx={{ mt: 2 }}
              options={bootcampOptions}
              getOptionLabel={(option) => option.name || ""}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                return [{ all: true, name: "Select All" }, ...filtered];
              }}
              value={formData.associatedBootcamps}
              onChange={handleBootcampChange}
              renderOption={(props, option) => {
                if (option.name === "No options") {
                  return (
                    <Box className="auto-complete-no-options">
                      <Box>{option?.name}</Box>
                    </Box>
                  );
                }
                if (option.all) {
                  const allSelected =
                    formData.associatedBootcamps.length === bootcampOptions.length;
                  return (
                    <li {...props} key="select-all">
                      <Box className="multi-tag-input-list-padding">
                        <Checkbox
                          className="auto-complete-checkbox-list"
                          size="small"
                          color="secondary"
                          checked={allSelected}
                        />
                        <Typography>Select All</Typography>
                      </Box>
                    </li>
                  );
                }
                const checked = formData.associatedBootcamps.some(
                  (b) => b.id === option.id
                );
                return (
                  <li
                    {...props}
                    key={option.id}
                    className={
                      checked ? "multi-tag-edit-list-selected" : "multi-tag-style"
                    }
                  >
                    <Box className="multi-tag-input-list-padding">
                      <Checkbox
                        className="auto-complete-checkbox-list"
                        size="small"
                        color="secondary"
                        checked={checked}
                      />
                      <Box className="flex-options">
                        <Box>{option?.name}</Box>
                        {option?.name === "Select All" ? null : (
                          <Box>
                            {option?.startDate
                              ? dayjs(option?.startDate)?.format("DD/MM/YYYY")
                              : null}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
              renderTags={(selected, getTagProps) => {
                if (selected.length === 0) return null;
                const firstTwo = selected.slice(0, 2);
                const remainingCount = selected.length - 2;

                return (
                  <>
                    {firstTwo.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        onDelete={() => handleBootcampChipDelete(option)}
                      />
                    ))}
                    {remainingCount > 0 && (
                      <Chip
                        label={`+${remainingCount}`}
                        sx={{
                          bgcolor: "#f1f1f1",
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Associated Bootcamps "
                  size="small"
                  color="secondary"
                  margin="dense"
                  required
                  error={!!errors.associatedBootcamps}
                  helperText={errors.associatedBootcamps}
                />
              )}
            />

            <Box mt={2}>
              <Typography>Description *</Typography>
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(val) => handleChange("description", val)}
                placeholder="Enter description..."
                required
                error={!!errors.description}
                helperText={errors.description}
              />
              {errors.description && (
                <Typography color="error" variant="font_12">
                  {errors.description}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: "0rem 2rem 1.875rem" }}>
          <Button className="block-button" variant="outlined" color="secondary" onClick={handleClose}>
            <Typography variant="font_12_bold_700">Cancel</Typography>
          </Button>

          <Button className="block-button" variant="contained" color="secondary" onClick={handleSubmit}>
            <Typography variant="font_12_bold_700">
              {isClone ? "Clone" : initialData ? "Update" : "Save"}
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormInputModal;
